import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFacilities, getFacilityId } from '../../../utils/getToken';
import { getFacilities as fetchFacilities } from '../../../screens/patient-screen/patientSaga';
import { componentKey } from '../../../screens/patient-screen/patientSlice';
import SelectDropdown from '../../Atoms/Select/Select';

interface FacilitySelectorProps {
  onFacilityChange?: (facilityId: string) => void;
  customStyle?: React.CSSProperties;
}

const FacilitySelector: React.FC<FacilitySelectorProps> = ({
  onFacilityChange,
  customStyle = {}
}) => {
  const dispatch = useDispatch();
  const { facilityData } = useSelector((state: any) => state[componentKey]);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>('');

  // Prefer the freshly-fetched list (so newly-added facilities show without a
  // re-login); fall back to the login (localStorage) list until the API returns.
  const facilities: any[] =
    facilityData && facilityData.length > 0 ? facilityData : getFacilities() || [];

  useEffect(() => {
    // Fetch the full, current facility list from the API. localStorage only
    // holds the facilities captured at login, so a facility added afterward
    // wouldn't appear without logging out/in.
    dispatch(fetchFacilities());

    // Initialise the selected facility from storage.
    const currentFacilityId = getFacilityId();
    if (currentFacilityId) {
      setSelectedFacilityId(currentFacilityId);
    } else {
      // If no facility ID is set, default to "All Facility" (empty value)
      setSelectedFacilityId('');
      // Ensure no facility ID is stored when defaulting to "All Facility"
      localStorage.removeItem('facilityId');
      sessionStorage.removeItem('facilityId');
    }
  }, []);

  const handleFacilityChange = (event: any) => {
    const newFacilityId = event?.target?.value;
    setSelectedFacilityId(newFacilityId);
    
    if (newFacilityId && newFacilityId.trim() !== '') {
      // Store facility ID in both localStorage and sessionStorage
      localStorage.setItem('facilityId', newFacilityId);
      sessionStorage.setItem('facilityId', newFacilityId);
    } else {
      // If "All Facility" is selected (empty value), remove facility ID from storage
      localStorage.removeItem('facilityId');
      sessionStorage.removeItem('facilityId');
    }
    
    // Call the callback if provided
    if (onFacilityChange) {
      onFacilityChange(newFacilityId);
    }
    
    // Reload the page to apply the new facility ID to all API calls
    window.location.reload();
  };

  const getSelectedFacilityName = () => {
    if (!selectedFacilityId || selectedFacilityId.trim() === '') {
      return 'All Facilities';
    }
    const selectedFacility = facilities?.find((facility: any) => facility?.id === selectedFacilityId);
    return selectedFacility ? selectedFacility?.name : 'Select Facility';
  };

  if (!facilities || facilities?.length === 0) {
    return null;
  }

  // Transform facilities data to match SelectDropdown format
  const facilityOptions = [
    { label: "All Facilities", value: "" },
    ...facilities.map((facility: any) => ({
      label: facility.name,
      value: facility.id
    }))
  ];

  return (
    <div style={{ maxWidth: "350px", ...customStyle }}>
      <SelectDropdown
        label=""
        name="facility"
        // placeholderValue="All Facility"
        displayEmpty={true}
        value={selectedFacilityId}
        options={facilityOptions}
        onChangeCb={handleFacilityChange}
        size="small"
      />
    </div>
  );
};

export default FacilitySelector;
